import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { Box, Checkbox, FormControlLabel, IconButton, Typography } from "@mui/material";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useGlobalManualLocationOrder } from "../../../contexts/Staffing/GlobalManualLocationOrderContext";
import { useUserSettingsContext } from "../../../contexts/Staffing/UserSettingsContext";
import { useSaveAsDefaultEnabled } from "../../../hooks/useSaveAsDefaultEnabled";
import type { DepartmentLocation } from "../../../utils/models";
import { getUniqueLocations } from "../../../utils/utilFunctions";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";
import { SaveAsDefaultDialog } from "../../SaveAsDefault/SaveAsDefaultDialog";
import { DraggableList } from "../../UIToolkit/DnD/DraggableList";
import { SystemReactTinyPopover } from "../../UIToolkit/Popups/SystemReactTinyPopover";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";

/**
 * Component to enable/disable manual ordering of Unique Locations and to reorder them via drag-and-drop.
 * The order is stored in the GlobalManualLocationOrderContext + DB user settings and is applied globally across the app.
 */
export const GlobalLocationOrder: FC = () => {
	const { t } = useTranslation();
	const saveAsDefaultEnabled = useSaveAsDefaultEnabled();
	const [isOpen, setIsOpen] = useState(false);
	const { chosenDepartment } = useHospitalData();
	const { enabled, setEnabled, orderedLocationNames, setOrderedLocationNames, cancelPendingUpdate } = useGlobalManualLocationOrder();
	const { updateSettings } = useUserSettingsContext();

	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const handleSaveAsDefault = useCallback(() => {
		setShowConfirmDialog(true);
	}, []);
	const onConfirmSaveAsDefault = useCallback(async () => {
		setShowConfirmDialog(false);
		cancelPendingUpdate();
		await updateSettings({ customLocationOrder: { enabled, orderedLocationNames } }, true);
		setIsOpen(false);
	}, [updateSettings, enabled, orderedLocationNames, cancelPendingUpdate]);

	const uniqueLocations = useMemo(() => getUniqueLocations(chosenDepartment.locations, false), [chosenDepartment.locations]);

	// Build the display list: use custom order if available, otherwise use uniqueLocations order
	const orderedLocations = useMemo(() => {
		if (orderedLocationNames.length === 0) return uniqueLocations;
		const locationMap = new Map(uniqueLocations.map((l) => [l.location, l]));
		const ordered: DepartmentLocation[] = [];
		for (const name of orderedLocationNames) {
			const loc = locationMap.get(name);
			if (loc) {
				ordered.push(loc);
				locationMap.delete(name);
			}
		}
		// Append any new locations not yet in the custom order
		for (const loc of locationMap.values()) {
			ordered.push(loc);
		}
		return ordered;
	}, [orderedLocationNames, uniqueLocations]);

	const handleReorder = (next: DepartmentLocation[]) => {
		setOrderedLocationNames(next.map((l) => l.location));
	};

	return (
		<>
			<SystemReactTinyPopover
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				positions={["bottom", "top"]}
				align="end"
				containerStyle={{ zIndex: "1301" }}
				content={
					<Box sx={{ minWidth: 240 }}>
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
							<FormControlLabel
								control={<Checkbox checked={enabled} onChange={(_, checked) => setEnabled(checked)} size="small" />}
								label={<Typography variant="body2">{t("location.order.custom")}</Typography>}
								sx={{ m: 0 }}
							/>
							<InHouseTooltip title={t("location.order.reset")}>
								<span>
									<IconButton size="small" disabled={!enabled} onClick={() => setOrderedLocationNames([])}>
										<RestartAltIcon fontSize="small" />
									</IconButton>
								</span>
							</InHouseTooltip>
						</Box>
						<Box
							sx={{
								maxHeight: { xs: 140, xl: 300 },
								overflow: "auto",
								overflowX: "hidden",
							}}
						>
							<DraggableList
								items={orderedLocations}
								getId={(loc) => loc.location}
								onChange={handleReorder}
								renderItem={({ item, handleListeners, isDragging }) => (
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 0.5,
											py: 0.5,
											px: 0.5,
											borderRadius: 1,
											opacity: isDragging ? 0.5 : enabled ? 1 : 0.5,
											bgcolor: isDragging ? "action.hover" : "transparent",
											pointerEvents: enabled ? "auto" : "none",
										}}
									>
										<IconButton size="small" {...handleListeners} sx={{ cursor: enabled ? "grab" : "default" }}>
											<DragIndicatorIcon fontSize="small" />
										</IconButton>
										<Typography variant="body2" noWrap>
											{item.location_short_name || item.location || t("location.order.no.location")}
										</Typography>
									</Box>
								)}
							/>
						</Box>
						{saveAsDefaultEnabled && (
							<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
								<SystemButton
									data-testid="location-order-save-as-default"
									buttonVariant="secondary"
									size="small"
									onClick={handleSaveAsDefault}
									sx={{ fontSize: "12px", py: 0.5, px: 1 }}
								>
									{t("settings.save.as.default")}
								</SystemButton>
							</Box>
						)}
					</Box>
				}
			>
				<InHouseTooltip title={t("location.order")}>
					<IconButton data-testid="global-location-order-button" onClick={() => setIsOpen((prev) => !prev)}>
						<SwapVertIcon sx={{ color: "primary.main" }} />
					</IconButton>
				</InHouseTooltip>
			</SystemReactTinyPopover>
			{saveAsDefaultEnabled && <SaveAsDefaultDialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)} onConfirm={onConfirmSaveAsDefault} />}
		</>
	);
};
